import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Clock, MapPin, User, Video, Building2, ExternalLink } from "lucide-react";
import { PageHeader } from "@/components/app/PageHeader";

const DAYS_EN = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAYS_MS = ["Isnin", "Selasa", "Rabu", "Khamis", "Jumaat", "Sabtu", "Ahad"];

function useDays() {
  const { lang } = useI18n();
  return lang === "ms" ? DAYS_MS : DAYS_EN;
}

function TimetableGrid({ classes }) {
  const days = useDays();
  const { t } = useI18n();
  const byDay = useMemo(() => {
    const map = Array.from({ length: 7 }, () => []);
    classes.forEach((c) => {
      if (c.day_of_week >= 0 && c.day_of_week < 7) map[c.day_of_week].push(c);
    });
    map.forEach((arr) => arr.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")));
    return map;
  }, [classes]);

  return (
    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {days.map((d, idx) => (
        <Card key={d} className="pmm-pop overflow-hidden rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`day-col-${idx}`} style={{ animationDelay: `${0.08 + idx * 0.05}s` }}>
          <CardContent className="p-4">
            <div className="mb-3 flex items-center justify-between rounded-xl bg-blue-950 px-3 py-2">
              <h3 className="font-display text-sm font-extrabold uppercase tracking-wide text-white">{d}</h3>
              <span className="num grid h-6 w-6 place-items-center rounded-full bg-yellow-400 text-xs font-extrabold text-blue-950">{byDay[idx].length}</span>
            </div>
            {byDay[idx].length === 0 ? (
              <p className="text-xs text-muted-foreground">—</p>
            ) : (
              <ul className="space-y-2">
                {byDay[idx].map((c) => (
                  <li key={c.id} className={`rounded-xl border-l-4 bg-white p-3 shadow-sm transition-all duration-300 hover:translate-x-1 hover:shadow-md ${c.mode === "online" ? "border-blue-700 bg-blue-50/50" : "border-orange-500 bg-orange-50/50"}`} data-testid={`class-${c.id}`}>
                    <div className="font-display font-extrabold text-blue-950">{c.name}</div>
                    <div className="num flex items-center gap-1 text-xs font-semibold text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {c.start_time} – {c.end_time}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span className="rounded-full bg-blue-800/10 px-2 py-0.5 font-bold text-blue-800">{c.level}</span>
                      <span
                        className={`flex items-center gap-1 rounded-full px-2 py-0.5 font-bold ${
                          c.mode === "online" ? "bg-blue-700/10 text-blue-700" : "bg-orange-500/10 text-orange-600"
                        }`}
                        data-testid={`class-mode-${c.id}`}
                      >
                        {c.mode === "online" ? <Video className="h-3 w-3" /> : <Building2 className="h-3 w-3" />}
                        {c.mode === "online" ? t("online") : t("physical")}
                      </span>
                      {c.room && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {c.room}</span>}
                      {c.teacher_name && <span className="flex items-center gap-1"><User className="h-3 w-3" /> {c.teacher_name}</span>}
                    </div>
                    {c.mode === "online" && c.meeting_link && (
                      <a
                        href={c.meeting_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-blue-900 to-blue-700 px-3 py-1 text-xs font-bold text-white shadow-sm shadow-blue-800/30 transition-transform duration-300 hover:scale-105"
                        data-testid={`join-link-${c.id}`}
                      >
                        <ExternalLink className="h-3 w-3" /> {t("joinClass")}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function AdminTimetablePage() {
  const { t, lang } = useI18n();
  const days = useDays();
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    level: "",
    day_of_week: "0",
    start_time: "16:00",
    end_time: "17:30",
    room: "",
    teacher_id: "",
    notes: "",
    mode: "physical",
    meeting_link: "",
  });

  const load = () => api.get("/classes").then((r) => setClasses(r.data.classes));
  useEffect(() => {
    load();
    api.get("/users").then((r) => setTeachers(r.data.users.filter((u) => u.role === "teacher"))).catch(() => {});
  }, []);

  const save = async () => {
    try {
      await api.post("/classes", {
        ...form,
        day_of_week: Number(form.day_of_week),
        teacher_id: form.teacher_id || null,
        meeting_link: form.mode === "online" ? form.meeting_link || null : null,
      });
      toast.success(lang === "ms" ? "Kelas disimpan" : "Class saved");
      setOpen(false);
      setForm({ name: "", level: "", day_of_week: "0", start_time: "16:00", end_time: "17:30", room: "", teacher_id: "", notes: "", mode: "physical", meeting_link: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm(lang === "ms" ? "Padam kelas ini?" : "Delete this class?")) return;
    try {
      await api.delete(`/classes/${id}`);
      load();
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-timetable">
      <PageHeader
        eyebrow={t("admin_dashboard_title")}
        title={t("timetable")}
        subtitle={lang === "ms" ? "Terbitkan jadual kelas mingguan untuk ibu bapa lihat." : "Publish the weekly class schedule for parents to see."}
        action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 px-6 font-display text-sm font-extrabold uppercase tracking-wide text-white shadow-lg shadow-orange-500/40 transition-transform duration-300 hover:scale-105" data-testid="create-class-btn"><Plus className="h-4 w-4" /> {t("addClass")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("addClass")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t("className")}</Label><Input data-testid="cls-name" className="h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Membaca Asas 1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("level")}</Label><Input data-testid="cls-level" className="h-11" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} placeholder="Tahap 1 (Asas)" /></div>
                <div>
                  <Label>{t("day")}</Label>
                  <Select value={form.day_of_week} onValueChange={(v) => setForm({ ...form, day_of_week: v })}>
                    <SelectTrigger data-testid="cls-day" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {days.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("startTime")}</Label><Input data-testid="cls-start" className="h-11" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
                <div><Label>{t("endTime")}</Label><Input data-testid="cls-end" className="h-11" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("classMode")}</Label>
                  <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                    <SelectTrigger data-testid="cls-mode" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="physical">{t("physical")}</SelectItem>
                      <SelectItem value="online">{t("online")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>{t("room")}</Label><Input data-testid="cls-room" className="h-11" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} placeholder={form.mode === "online" ? "—" : "Bilik A"} /></div>
              </div>
              {form.mode === "online" && (
                <div>
                  <Label>{t("meetingLink")}</Label>
                  <Input data-testid="cls-meeting-link" className="h-11" value={form.meeting_link} onChange={(e) => setForm({ ...form, meeting_link: e.target.value })} placeholder="https://meet.google.com/…" />
                </div>
              )}
              <div>
                <Label>{t("teacher")}</Label>
                <Select value={form.teacher_id || "__none__"} onValueChange={(v) => setForm({ ...form, teacher_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="cls-teacher" className="h-11"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {teachers.map((tr) => <SelectItem key={tr.id} value={tr.id}>{tr.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={save} data-testid="cls-save" className="h-11 w-full rounded-full bg-gradient-to-r from-orange-600 to-orange-500 font-display font-extrabold uppercase tracking-wide text-white">{t("submit")}</Button>
            </div>
          </DialogContent>
        </Dialog>
        }
      />

      <TimetableGrid classes={classes} />

      {classes.length > 0 && (
        <div className="pmm-pop rounded-[1.5rem] border border-border/60 bg-white p-5 shadow-lg" style={{ animationDelay: "0.2s" }}>
          <h2 className="mb-3 font-display text-sm font-extrabold uppercase tracking-widest text-blue-950">
            {lang === "ms" ? "Padam kelas" : "Delete classes"}
          </h2>
          <div className="flex flex-wrap gap-2">
            {classes.map((c) => (
              <Button
                key={c.id}
                variant="outline"
                size="sm"
                onClick={() => del(c.id)}
                className="rounded-full border-blue-900/20 font-semibold text-blue-950 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                data-testid={`del-class-${c.id}`}
              >
                {c.name} · {days[c.day_of_week]} · {c.start_time} ×
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ParentTimetablePage() {
  const { t, lang } = useI18n();
  const [classes, setClasses] = useState([]);
  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data.classes)); }, []);
  return (
    <div className="space-y-6" data-testid="parent-timetable">
      <PageHeader
        eyebrow="Kelas Program Membaca PMM"
        title={t("timetable")}
        subtitle={lang === "ms" ? "Jadual kelas untuk anak-anak anda mengikut kelas mereka." : "Weekly schedule for your children based on their level."}
      />
      {classes.length === 0 ? (
        <Card className="pmm-pop rounded-[1.5rem] border-border/60 shadow-md"><CardContent className="p-6 text-sm font-semibold text-muted-foreground">
          {lang === "ms"
            ? "Tiada kelas untuk kelas anak anda buat masa ini."
            : "No classes for your children's levels yet."}
        </CardContent></Card>
      ) : (
        <TimetableGrid classes={classes} />
      )}
    </div>
  );
}
